/**
 * Which muscle group a SHAPE of the silhouette belongs to — the inverse of `shared/ui/bodyModel`'s
 * `REGIONS` — so a press on the figure can mean the same as a press on that muscle's chip.
 *
 * Owner decision D21 (18 September 2026): on the creator's step 2 a trainer clicks a muscle on the
 * body itself. Built from `REGIONS`, the one table that says where each group IS, rather than
 * restated: a second table would be a second opinion about the same artwork.
 *
 * Per VIEW, because `REGIONS` is: `Shoulders` paints the front deltoid only, so the back view's
 * deltoid answers `null` — pressing a shape that lights nothing selects nothing. `FullBody` and
 * `Other` have no anatomy and are reachable from their chips alone.
 */

import { REGIONS } from '~/shared/ui/bodyModel/regions';
import type { BodySlug } from '~/shared/ui/bodyModel/bodyArtwork';

import { muscleGroupFromApi, muscleGroupToApi, type MuscleGroup } from './muscleGroup';

type Side = 'front' | 'back';

/** The group a shape on this view belongs to, or `null` for a shape no group paints. */
export function muscleGroupOfSlug(slug: BodySlug, side: Side): MuscleGroup | null {
  for (const [wire, regions] of Object.entries(REGIONS)) {
    if (regions.some((region) => region.muscle === slug && region.side === side)) {
      return muscleGroupFromApi(wire);
    }
  }
  return null;
}

/** Every shape on this view one group paints — what a hover over any of them lights together. */
export function slugsOfMuscleGroup(group: MuscleGroup, side: Side): readonly BodySlug[] {
  const wire = muscleGroupToApi(group);
  if (!Object.hasOwn(REGIONS, wire)) return [];
  return (REGIONS[wire] ?? []).filter((region) => region.side === side).map((r) => r.muscle);
}

/**
 * Whether the figures light the WHOLE silhouette: a movement that names „Całe ciało" and nothing
 * else (polish of 4 November 2026). `FullBody` has no shapes in `REGIONS`, so it lit nothing and two
 * empty figures read as a render that failed; beside any real attribution the whole body would
 * drown it, so only when it is the one group named.
 */
export function lightsWholeBody(
  primary: readonly MuscleGroup[],
  secondary: readonly MuscleGroup[],
): boolean {
  return primary.length === 1 && primary[0] === 'fullBody' && secondary.length === 0;
}
